import React, { useState, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { fetchProjects, createProject, updateProject, deleteProject, reorderProjects } from '../../api';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

export default function ManageProjects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    link: '',
    category: 'General'
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitStatus, setSubmitStatus] = useState('idle');
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [isNewCategory, setIsNewCategory] = useState(false);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const data = await fetchProjects();
      setProjects(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(projects);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Optimistically update local state
    setProjects(items);

    // Save to backend
    const orderings = items.map((project, index) => ({
      id: project.id,
      order: index
    }));

    try {
      await reorderProjects(orderings);
    } catch (error) {
      console.error("Failed to save order:", error);
      alert('Failed to save new order. Reverting...');
      loadProjects(); 
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const onDrop = useCallback(acceptedFiles => {
    const file = acceptedFiles[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
    multiple: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus('loading');
    
    try {
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('description', formData.description);
      if (formData.link) submitData.append('link', formData.link);
      submitData.append('category', formData.category || 'General');
      if (imageFile) submitData.append('image', imageFile);

      if (editingProjectId) {
        await updateProject(editingProjectId, submitData);
      } else {
        await createProject(submitData);
      }
      
      setFormData({ title: '', description: '', link: '', category: 'General' });
      setImageFile(null);
      setImagePreview(null);
      setEditingProjectId(null);
      setIsNewCategory(false);
      setSubmitStatus('success');
      loadProjects();
      setTimeout(() => setSubmitStatus('idle'), 3000);
    } catch (error) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 3000);
    }
  };

  const handleEditClick = (project) => {
    setEditingProjectId(project.id);
    setFormData({
      title: project.title || '',
      description: project.description || '',
      link: project.link || '',
      category: project.category || 'General'
    });
    setImagePreview(project.imageUrl || null);
    setImageFile(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEdit = () => {
    setEditingProjectId(null);
    setFormData({ title: '', description: '', link: '', category: 'General' });
    setImagePreview(null);
    setImageFile(null);
    setIsNewCategory(false);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        await deleteProject(id);
        loadProjects();
      } catch (error) {
        console.error(error);
        alert('Failed to delete project');
      }
    }
  };

  return (
    <div className="flex flex-col gap-6 lg:gap-8 text-[#f1edfd]">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl lg:text-3xl font-Poppins font-bold text-center lg:text-left w-full lg:w-auto">Manage Projects</h1>
      </div>

      {/* Add/Edit Project Form */}
      <div className="bg-[#1a1a1c] border border-gray-800 p-6 rounded-2xl">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-Poppins font-semibold">
            {editingProjectId ? 'Edit Project' : 'Add New Project'}
          </h2>
          {editingProjectId && (
            <button 
              onClick={cancelEdit}
              className="text-gray-400 hover:text-[#f1edfd] text-sm"
            >
              Cancel Editing
            </button>
          )}
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 flex flex-col gap-2">
              <label className="text-sm text-gray-400">Title</label>
              <input 
                type="text" 
                name="title" 
                value={formData.title} 
                onChange={handleChange} 
                required
                className="bg-[#131315] border border-gray-800 rounded-lg px-4 py-3 text-[#f1edfd] focus:outline-none focus:border-[#3aa1ff]"
                placeholder="Project Name"
              />
            </div>
            <div className="flex-1 flex flex-col gap-2">
              <label className="text-sm text-gray-400">Project URL (Optional)</label>
              <input 
                type="url" 
                name="link" 
                value={formData.link} 
                onChange={handleChange} 
                className="bg-[#131315] border border-gray-800 rounded-lg px-4 py-3 text-[#f1edfd] focus:outline-none focus:border-[#3aa1ff]"
                placeholder="https://..."
              />
            </div>
            <div className="flex-1 flex flex-col gap-2">
              <label className="text-sm text-gray-400">Category</label>
              {isNewCategory ? (
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="flex-1 bg-[#131315] border border-gray-800 rounded-lg px-4 py-3 text-[#f1edfd] focus:outline-none focus:border-[#3aa1ff]"
                    placeholder="Nouvo kategori..."
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => { setIsNewCategory(false); setFormData(p => ({ ...p, category: 'General' })); }}
                    className="px-3 py-2 text-gray-400 hover:text-white border border-gray-700 rounded-lg text-sm"
                  >✕</button>
                </div>
              ) : (
                <select
                  name="category"
                  value={formData.category}
                  onChange={(e) => {
                    if (e.target.value === '__new__') {
                      setIsNewCategory(true);
                      setFormData(p => ({ ...p, category: '' }));
                    } else {
                      handleChange(e);
                    }
                  }}
                  className="bg-[#131315] border border-gray-800 rounded-lg px-4 py-3 text-[#f1edfd] focus:outline-none focus:border-[#3aa1ff]"
                >
                  {[...new Set(projects.map(p => p.category || 'General'))].map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                  {!projects.some(p => (p.category || 'General') === formData.category) && formData.category && (
                    <option value={formData.category}>{formData.category}</option>
                  )}
                  <option value="__new__">+ Nouvo kategori...</option>
                </select>
              )}
            </div>
          </div>
          
          <div className="flex flex-col gap-2">
            <label className="text-sm text-gray-400">Project Image (Drag & Drop)</label>
            <div 
              {...getRootProps()} 
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                isDragActive ? 'border-[#3aa1ff] bg-[#3aa1ff]/5' : 'border-gray-700 bg-[#131315] hover:border-gray-500 hover:bg-gray-800/50'
              }`}
            >
              <input {...getInputProps()} />
              {imagePreview ? (
                <div className="flex flex-col items-center gap-4">
                  <img src={imagePreview} alt="Preview" className="h-32 rounded-lg object-contain" />
                  <p className="text-sm text-[#3aa1ff]">Click or drag to replace image</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2 text-gray-400">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-10 h-10 mb-2 text-gray-500">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                  </svg>
                  <p className="font-medium text-[#f1edfd]">Drag & drop an image here</p>
                  <p className="text-sm">or click to select a file</p>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm text-gray-400">Description</label>
            <textarea 
              name="description" 
              value={formData.description} 
              onChange={handleChange} 
              required
              rows="3"
              className="bg-[#131315] border border-gray-800 rounded-lg px-4 py-3 text-[#f1edfd] focus:outline-none focus:border-[#3aa1ff] resize-none"
              placeholder="Describe the project..."
            ></textarea>
          </div>

          <div className="flex gap-4 mt-2">
            <button 
              type="submit" 
              disabled={submitStatus === 'loading'}
              className="flex-1 bg-[#3aa1ff] text-[#131315] font-Poppins font-medium py-3 px-6 rounded-lg hover:bg-[#288ce6] transition-colors disabled:opacity-50"
            >
              {submitStatus === 'loading' ? (editingProjectId ? 'Updating...' : 'Adding...') : (editingProjectId ? 'Update Project' : 'Add Project')}
            </button>
            {editingProjectId && (
              <button 
                type="button"
                onClick={cancelEdit}
                className="flex-1 bg-gray-800 text-[#f1edfd] font-Poppins font-medium py-3 px-6 rounded-lg hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
            )}
          </div>
          
          {submitStatus === 'success' && <p className="text-green-500 text-sm mt-2">{editingProjectId ? 'Project updated successfully!' : 'Project added successfully!'}</p>}
          {submitStatus === 'error' && <p className="text-red-500 text-sm mt-2">{editingProjectId ? 'Failed to update project.' : 'Failed to add project.'}</p>}
        </form>
      </div>

      {/* Projects List with Drag and Drop */}
      <div className="bg-[#1a1a1c] border border-gray-800 p-6 rounded-2xl">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-Poppins font-semibold">Current Projects</h2>
          <p className="text-sm text-gray-500 italic">Drag handles to reorder</p>
        </div>
        
        {loading ? (
          <div className="text-gray-400 text-center py-10">Loading projects...</div>
        ) : projects.length === 0 ? (
          <div className="text-gray-400 text-center py-10">No projects found. Add one above.</div>
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="projects-list">
              {(provided) => (
                <div 
                  {...provided.droppableProps} 
                  ref={provided.innerRef}
                  className="grid grid-cols-1 md:grid-cols-2 gap-4"
                >
                  {projects.map((project, index) => (
                    <Draggable key={project.id.toString()} draggableId={project.id.toString()} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`bg-[#131315] border rounded-xl p-4 flex flex-col sm:flex-row gap-4 transition-all ${
                            snapshot.isDragging ? 'border-[#3aa1ff] shadow-lg z-50 bg-[#1a1a1c]' : 'border-gray-800'
                          }`}
                        >
                          {/* Drag Handle */}
                          <div 
                            {...provided.dragHandleProps} 
                            className="text-gray-600 hover:text-gray-400 cursor-grab active:cursor-grabbing flex items-center justify-center sm:justify-start"
                          >
                            <DragIndicatorIcon />
                          </div>

                          {project.imageUrl && (
                            <div className="w-full sm:w-20 h-40 sm:h-20 flex-shrink-0">
                                <img src={project.imageUrl} alt={project.title} className="w-full h-full object-cover rounded-lg bg-gray-800" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <h3 className="font-bold text-lg truncate">{project.title}</h3>
                            <p className="text-[#3aa1ff] text-xs font-medium uppercase tracking-wider">{project.category || 'General'}</p>
                            <p className="text-gray-400 text-sm line-clamp-2 mt-1">{project.description}</p>
                            <div className="flex items-center gap-4 mt-3">
                              <button 
                                onClick={() => handleEditClick(project)}
                                className="text-[#3aa1ff] hover:text-[#288ce6] text-sm font-medium"
                              >
                                Edit
                              </button>
                              <button 
                                onClick={() => handleDelete(project.id)}
                                className="text-red-400 hover:text-red-300 text-sm font-medium"
                              >
                                Delete
                              </button>
                              {project.link && (
                                <a href={project.link} target="_blank" rel="noreferrer" className="text-[#3aa1ff] hover:text-[#288ce6] text-sm font-medium">
                                  View Link
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>
    </div>
  );
}
